/**
 * THE CACHE KEY REGISTRY — every key in the app is defined exactly once, here.
 *
 * The reason is refactor safety, not tidiness. A producer (`useQuery`) and an invalidator
 * (`invalidateQueries` after a mutation) have to agree on a key, and when they are written
 * as inline arrays in two different files they agree only until someone renames one of
 * them. Nothing fails: the query keeps caching, the invalidation keeps "succeeding", and a
 * screen quietly shows stale data — which on a phone people read as the app being broken.
 *
 * Sharing one symbol makes that rename impossible to get half-right, and `no-inline-query-keys`
 * (eslint-rules/) fails the build if anyone writes a key by hand.
 *
 * SHAPE: broadest first, so `invalidateQueries({ queryKey: queryKeys.notes.all })` catches
 * every list and detail beneath it. That prefix relationship IS the invalidation design —
 * treat the nesting as meaningful, not decorative.
 */
/**
 * WRITES ARE NAMED HERE TOO, and that is not decoration.
 *
 * A `mutationKey` costs nothing in the mutation cache — it is not an invalidation target —
 * but it is what lets a failed WRITE report which domain and which action failed. The
 * alternative was a `meta.operation` string restated beside every hook, which is a second
 * spelling of a name that already exists and a thing to forget on the next hook. Reading
 * both from segments 0 and 1 of one key is what makes the report unforgettable, so every
 * key here — read or write — is `[domain, action, ...]`.
 */
export const queryKeys = {
  /**
   * 🚨 `session` IS THE SIGNED-IN SESSION. `sessions` BELOW IS A WORKOUT. READ THE S.
   *
   * Two namespaces one letter apart, and nothing executable can tell them apart for you:
   * `no-inline-query-keys` proves a key came from this file, never that it came from the
   * RIGHT entry, and both spellings type-check at every call site. This comment and its twin
   * below are the whole defence.
   *
   * THIS one is authentication — who is signed in, signing in, signing out. There is no
   * plural of it and there never will be.
   */
  session: {
    all: ["session"] as const,
    current: () => ["session", "current"] as const,
    signIn: () => ["session", "sign-in"] as const,
    signUp: () => ["session", "sign-up"] as const,
    signInWithProvider: () => ["session", "sign-in-with-provider"] as const,
    signOut: () => ["session", "sign-out"] as const,
  },
  notes: {
    all: ["notes"] as const,
    /**
     * ONE entry for the whole list, however many pages are loaded. The cursor lives in the
     * infinite query's page params, never here: a cursor in the key makes every page its
     * own cache entry, so an invalidation reaches one of them and an optimistic insert on
     * page one is stranded there while page two carries a stale copy of the same rows.
     */
    list: () => ["notes", "list"] as const,
    detail: (id: string) => ["notes", "detail", id] as const,
    create: () => ["notes", "create"] as const,
    remove: () => ["notes", "remove"] as const,
  },
  devices: {
    all: ["devices"] as const,
    register: () => ["devices", "register"] as const,
  },
  meta: {
    all: ["meta"] as const,
    minimumVersion: () => ["meta", "minimum-version"] as const,
  },

  /* ---------------------------------------------------------------------------------------
   * BUDDY'S OWN DOMAINS.
   *
   * Registered by BUD-282 with the app shell, BEFORE the queries that use them exist. That
   * is the point of putting them here early: four screen issues and four service issues land
   * in parallel on top of this frame, and a key invented independently in each of them is a
   * key that four hooks and four invalidations only agree on by luck.
   *
   * Nothing reads these yet. The services are BUD-278 (sessions), BUD-279 (athlete),
   * BUD-280 (gyms) and BUD-281 (logs); the screens are BUD-283 and BUD-287..292.
   * ------------------------------------------------------------------------------------ */

  /**
   * 🚨 `sessions` IS A WORKOUT. `session` ABOVE IS WHO IS SIGNED IN. READ THE S.
   *
   * A suggestion, the one you started, the one you are in the middle of. Nothing here has
   * anything to do with authentication, and reaching for the wrong one of these two fails
   * silently: the query caches, the invalidation "succeeds", and a screen shows stale data.
   */
  sessions: {
    all: ["sessions"] as const,
    /**
     * ONE entry for the whole deck, however many pages are loaded — the same rule as
     * `notes.list` above and for the same reason: a cursor in the key makes every page its
     * own cache entry, so one invalidation reaches one page and the rest go stale in place.
     *
     * ⚠️ AND THERE IS DELIBERATELY NO `skip` KEY. Skipping a suggestion means nothing in v1
     * (BUD-282): the deck is drawn from a seed cursor, so moving past a card is a scroll
     * position, not a write anybody has to remember.
     */
    suggestions: () => ["sessions", "suggestions"] as const,
    detail: (id: string) => ["sessions", "detail", id] as const,
    start: () => ["sessions", "start"] as const,
    /**
     * STOPPING ONE ON PURPOSE (BUD-344). Not an invalidation target — it is what lets a failed
     * cancel say what failed: the cache's failure handler reads segments 0 and 1, so a failed
     * cancel is reported as domain `sessions`, action `cancel` rather than anonymously. The
     * sentence the athlete reads comes from the error CODE, as it does everywhere else.
     */
    cancel: () => ["sessions", "cancel"] as const,
    /**
     * WHICH SHAPES FIT TODAY, AND WHY THE REST DO NOT — the shape picker's summary line.
     *
     * ⚠️ THE WISHES ARE IN THE KEY, unlike the deck's cursor, and the reason is the opposite of
     * the one that keeps a cursor out: two wish sets are two ANSWERS to two questions, not two
     * pages of one list. The picker moves a toggle and the counts move with it, so an entry per
     * wish set is exactly what makes the previous answer still there when a toggle goes back.
     *
     * Under `sessions` and not `shapes` because the URL is `/v1/sessions/shape-fit` — this is
     * the draw's question asked without drawing, and it is per athlete. `shapes` publishes the
     * library, which is the same thirty rows for everybody.
     */
    shapeFit: (want: readonly string[], avoid: readonly string[]) =>
      ["sessions", "shape-fit", want.join(","), avoid.join(",")] as const,
    /**
     * WHAT COULD GO IN AN EMPTY SLOT — the add-work sheet's offers (BUD-345).
     *
     * ⚠️ EVERY TERM THE OFFERS WERE COMPOSED UNDER IS IN THE KEY, and none of them is a page
     * of one list: the same seed answers DIFFERENT offers for a different slot, a different
     * gear, or a clock that has already run. Two of those are two answers to two questions, so
     * an entry each is what makes the strength sheet still there when the accessory sheet
     * closes — and what stops a started clock being served the sheet it narrowed.
     */
    work: (seed: number, slot: string, gear: string, metconStarted: boolean) =>
      ["sessions", "work", seed, slot, gear, metconStarted] as const,
    /**
     * THE FULLER LIST BEHIND `Browse` (BUD-346). The two tabs are in the key for the same
     * reason the wishes are in `shapeFit`'s: moving a tab asks a new question, and an entry per
     * answer is what makes the previous one still there when the tab goes back.
     *
     * `null` is the design's preselected `All` — the ABSENCE of a filter, spelled once here
     * rather than as a sentinel word the service would then have to disambiguate.
     */
    lifts: (
      seed: number,
      gear: string,
      metconStarted: boolean,
      pattern: string | null,
      intent: string | null,
    ) => ["sessions", "lifts", seed, gear, metconStarted, pattern, intent] as const,
    /**
     * TAKING ONE. Not an invalidation target — it is what lets a failed add say what failed:
     * the cache's failure handler reads segments 0 and 1, so this reports as domain `sessions`,
     * action `add-block` rather than anonymously.
     */
    addBlock: () => ["sessions", "add-block"] as const,
    /** Taking one back out — the same rule as `addBlock`: a name for the failure report. */
    removeBlock: () => ["sessions", "remove-block"] as const,
    /**
     * WHAT ELSE COULD GO ON ONE LINE (BUD-348) — the equivalents screen, frame `6a`.
     *
     * ⚠️ THE SEED, THE GEAR AND THE LINE ARE ALL IN THE KEY, and none of the three is optional.
     * The answer is a pure function of the three plus the athlete's own state: a different line
     * is a different question, and the same line at a different column is a different set of
     * WEIGHTS. Keying on the session id instead would make the editor's second line read the
     * first line's answer out of the cache.
     */
    equivalents: (seed: number, gear: string, line: number) =>
      ["sessions", "equivalents", seed, gear, line] as const,
    /**
     * KEEPING ONE (BUD-347). Not an invalidation target — it is what lets a failed save say what
     * failed: the cache's failure handler reads segments 0 and 1, so it is reported as domain
     * `sessions`, action `swap-line` rather than anonymously. Same device as `cancel` above.
     */
    swapLine: () => ["sessions", "swap-line"] as const,
  },

  /**
   * THE SHAPE LIBRARY — the thirty shapes and the five structures, as published.
   *
   * ⚠️ NOT UNDER `sessions`, EVEN THOUGH ONLY THE SHAPE PICKER READS IT. One domain is one name
   * in five places and `GET /v1/shapes` is its own domain on the service side; a key that named
   * somebody else's domain would make `queryKeys.shapes.all` unable to invalidate its own read.
   * It is also the half that is the same for every athlete, which is why it caches for a deploy
   * and the fit beside it caches for a keystroke.
   */
  shapes: {
    all: ["shapes"] as const,
    catalogue: () => ["shapes", "catalogue"] as const,
  },

  /** What actually happened: history, the debrief write, benchmarks, and the heart. */
  logs: {
    all: ["logs"] as const,
    list: () => ["logs", "list"] as const,
    benchmarks: () => ["logs", "benchmarks"] as const,
    /**
     * THE WINDOW THE HISTORY CHART AND THE TWO STAT CARDS ARE COMPUTED FROM — added by
     * BUD-290, the first code to read this namespace.
     *
     * It is the same endpoint as `list` asked a different question: one bounded read of the
     * hundred most recent logs, so seven weeks of bars and a month's count are answered
     * together and do not change while the list underneath them is scrolled. A separate
     * entry rather than a page of `list` because the two have different lifetimes and
     * different sizes — and because `logs.all` is a prefix of both, so one invalidation
     * after the heart or a debrief refreshes the chart and the list at once.
     */
    trend: () => ["logs", "trend"] as const,
    /**
     * A YEAR OF TRAINING DAYS — the grid at the top of history.
     *
     * Its own entry rather than a shape on `trend`: it is one aggregate row per day over a year
     * against a hundred whole logs, so the two have different sizes and different lifetimes.
     * `logs.all` is a prefix of both, so a debrief refreshes them together.
     */
    activity: () => ["logs", "activity"] as const,
    /**
     * ONE LOGGED SESSION, WHOLE — the row and its splits, which no list carries. Added for the
     * screen a history row opens; `logs.all` is a prefix of it, so the heart and a debrief
     * refresh it along with everything else in this namespace.
     */
    detail: (id: string) => ["logs", "detail", id] as const,
    create: () => ["logs", "create"] as const,
    favourite: () => ["logs", "favourite"] as const,
  },

  /**
   * EVERY MOVEMENT THE ATHLETE HAS PERFORMED — the other axis through the same sets.
   *
   * 🚨 IT IS ITS OWN NAMESPACE AND NOT A CORNER OF `logs`, and the prefix relationship is why
   * that matters here. A log is one SESSION and is invalidated by the debrief that wrote it; a
   * movement's history is an aggregate ACROSS logs and is invalidated by ANY debrief that
   * touched that movement. Nested under `logs`, one invalidation would either refresh far too
   * much or — worse — a movement chart would sit stale behind a session that has just been
   * written, showing an athlete a total that does not include the sets they just did.
   *
   * `detail` KEYS ON THE WINDOW AS WELL AS THE MOVEMENT, which is the opposite of the cursor
   * rule stated on `notes.list` and right for the same underlying reason. There, a cursor in
   * the key fragments ONE list across pages. Here there is no list: three months and three
   * years are two different ANSWERS to two different questions, and sharing one entry would
   * make the cache hand back a three-month chart to a screen asking for three years while the
   * chips said otherwise.
   */
  movements: {
    all: ["movements"] as const,
    /** The library — everything performed, ever. One request; the screen filters it locally. */
    list: () => ["movements", "list"] as const,
    detail: (movement: string, range: string, bucket: string) =>
      ["movements", "detail", movement, range, bucket] as const,
    /**
     * WHAT THE ATHLETE ACTUALLY DID, DAY BY DAY — one entry per MOVEMENT, however many pages
     * are loaded (BUD-387).
     *
     * ⚠️ THE MOVEMENT IS IN THE KEY AND THE CURSOR IS NOT, and the two halves of that are
     * different rules rather than one. The movement is in the key because two movements are two
     * different lists, exactly as `detail` keys on its window. The cursor stays in the infinite
     * query's page params because those pages are ONE list: a cursor in the key makes every page
     * its own cache entry, so `movements.all` — which is what a debrief invalidates — would reach
     * page one and leave the rest stale in place behind it.
     *
     * ⚠️ AND IT DOES NOT KEY ON THE RANGE, unlike `detail`. This list is not windowed: the chip
     * in the header governs the chart above it, and a set an athlete performed does not stop
     * having happened because the chart is showing three months.
     */
    sessions: (movement: string) => ["movements", "sessions", movement] as const,
  },

  /**
   * The limits the engine plans around: maxes, skills, ladders, restrictions.
   *
   * ⚠️ `profile` WAS `limits`, RENAMED BY BUD-292 — the first code to read it.
   *
   * The key was registered with the app shell while the endpoint was still going to be
   * `GET /v1/athlete/limits`. It is not: the service answers the whole athlete at
   * `GET /v1/athlete`, and `api/src/domains/athlete/routes.ts` says at length why — a
   * `/limits` that hands back body mass, skills, ladders AND restrictions makes the URL lie
   * about its own payload. A cache key that names one of five tables lies the same way, and
   * this one entry holds all five. Renamed while it was still free: nothing read it.
   *
   * The five writes are named here for the same reason every write in this file is — a
   * `mutationKey` is what lets a failed write say WHICH write failed. Segments 0 and 1 of
   * `["athlete", "set-ladder"]` are the domain and the action in the report.
   */
  athlete: {
    all: ["athlete"] as const,
    profile: () => ["athlete", "profile"] as const,
    /** Sex, body mass and self-rated level — written by first run (BUD-293) and nowhere else. */
    setBody: () => ["athlete", "set-body"] as const,
    setLimit: () => ["athlete", "set-limit"] as const,
    removeLimit: () => ["athlete", "remove-limit"] as const,
    setSkill: () => ["athlete", "set-skill"] as const,
    removeSkill: () => ["athlete", "remove-skill"] as const,
    setLadder: () => ["athlete", "set-ladder"] as const,
    addRestriction: () => ["athlete", "add-restriction"] as const,
    removeRestriction: () => ["athlete", "remove-restriction"] as const,
    /**
     * THE THREE WISH WRITES (BUD-341/342). Every one of them changes what the deck may draw —
     * see the invalidation in `athlete.queries.ts`, which reaches `sessions.all` as well as
     * this domain's own profile.
     */
    setWish: () => ["athlete", "set-wish"] as const,
    removeWish: () => ["athlete", "remove-wish"] as const,
    setShapeFilter: () => ["athlete", "set-shape-filter"] as const,
    /**
     * ANSWERING WHAT THE SESSION SCREEN OFFERED (BUD-396 · BUD-384). Not invalidation targets —
     * they are what let a failed answer say WHICH answer failed: the cache's failure handler
     * reads segments 0 and 1, so a failed decline reports as domain `athlete`, action
     * `answer-retest` rather than anonymously.
     *
     * ⚠️ UNDER `athlete` THOUGH THE SESSION SCREEN MAKES THEM, because the row they write is
     * `athlete_working_offsets` and the URL is `/v1/athlete/…` — one domain is one name in five
     * places. The session screen reads one feature and writes another, which is exactly what the
     * barrel rule produces: a query hook crosses, a service never does.
     */
    answerRetest: () => ["athlete", "answer-retest"] as const,
    answerDeload: () => ["athlete", "answer-deload"] as const,
  },

  /** The rooms you train in, and the equipment each one has. */
  gyms: {
    all: ["gyms"] as const,
    list: () => ["gyms", "list"] as const,
    /**
     * The EDITOR's read — one room WITH its rack. Separate from `list` because the service
     * answers them with two different field sets on purpose: the picker gets names, the
     * editor gets every plate. `all` is a prefix of both, so one invalidation after a write
     * refreshes the card the athlete is looking at and the list underneath it.
     */
    detail: (id: string) => ["gyms", "detail", id] as const,
    create: () => ["gyms", "create"] as const,
    /**
     * PROMOTING A ROOM TO DEFAULT — a `PATCH` with `isDefault: true`, named separately from
     * `update` because it is the write that rebuilds the deck (BUD-291) and a failed one has
     * to be able to say which of the two failed.
     */
    setDefault: () => ["gyms", "set-default"] as const,
    update: () => ["gyms", "update"] as const,
    setEquipment: () => ["gyms", "set-equipment"] as const,
    removeEquipment: () => ["gyms", "remove-equipment"] as const,
  },

  /**
   * THE ENGINE BENCH — a DEVELOPMENT-ONLY screen (BUD-312), and its keys live here for the
   * same reason every other key does: the rule is that no key is written inline, and a
   * dev-only exception to it would be the first inline key in the codebase.
   *
   * `offer` KEYS ON ALL FOUR REQUEST INPUTS, which is the opposite of the cursor rule stated
   * on `notes.list` above and is right for the same underlying reason. There, a cursor in the
   * key fragments ONE list across pages. Here there is no list: an offer is a pure function of
   * these four values, two different seeds are two different answers, and RE-ROLL is a new
   * seed. Sharing one entry would make the cache lie about which reading is on the screen.
   */
  bench: {
    all: ["bench"] as const,
    presets: () => ["bench", "presets"] as const,
    offer: (athlete: string, room: string, seed: number, minutes: number) =>
      ["bench", "offer", athlete, room, seed, minutes] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;
